import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Typography,
} from '@mui/material';

import ExpandableSection from '../../../../Components/ExpandableSection';
import IroColorPicker from '../../../../Partials/IroColorPicker';

import PepperDeviceTemplate, { ConfigProps } from './Template';

export default function RGBStripValidation(props: ValidationProps) {
  return (
    <PepperDeviceTemplate<RGBStripConfig>
      moveClient={props.moveClient}
      onChangeDevice={props.onChangeDevice}
      device={props.device}
      Config={RGBStrip}
    />
  );
}

//----------------------------------------------------------------------------------------------------------------------

const sceneModeOptions = [
  { key: 'None', value: 0 },
  { key: 'Music App Sync', value: 25 },
];

const ledPatternOptions = [
  { key: 'none', value: 'none' },
  { key: 'night', value: 'night' },
  { key: 'reading', value: 'reading' },
  { key: 'cocktail', value: 'cocktail' },
  { key: 'leisure', value: 'leisure' },
  { key: 'soft', value: 'soft' },
  { key: 'blinking', value: 'blinking' },
  { key: 'rainbow', value: 'rainbow' },
  { key: 'rave', value: 'rave' },
  { key: 'nature', value: 'nature' },
];

//----------------------------------------------------------------------------------------------------------------------

const RGBStrip: React.FC<ConfigProps<RGBStripConfig>> = (props: {
  device: MoveDevice;
  config: RGBStripConfig;
  onConfigChange: (moveData: Partial<RGBStripConfig>) => void;
}) => {
  const getBrightnessConfig = () => {
    return (
      <Box width={1}>
        <Typography variant="caption">Brightness</Typography>
        <Box px={2}>
          <Slider
            color="secondary"
            value={props.config.bulbBrightness || 0}
            onChangeCommitted={(
              event: React.SyntheticEvent | Event,
              value: number | number[],
            ) => {
              props.onConfigChange({
                bulbBrightness: value as number,
              });
            }}
            step={1}
            valueLabelDisplay="auto"
            marks={[
              { value: 0, label: '0' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
            ]}
          />
        </Box>
      </Box>
    );
  };

  const getTemperatureConfig = () => {
    return (
      <Box width={1}>
        <Typography variant="caption">Color Temperature</Typography>
        <Box px={2}>
          <Slider
            color="secondary"
            value={props.config.bulbColorTemp || 2700}
            onChangeCommitted={(
              event: React.SyntheticEvent | Event,
              value: number | number[],
            ) => {
              props.onConfigChange({
                bulbColorTemp: value as number,
                bulbColor: '0',
              });
            }}
            step={1}
            valueLabelDisplay="auto"
            marks={[
              { value: 2700, label: '2700' },
              { value: 6500, label: '6500' },
            ]}
            min={2700}
            max={6500}
          />
        </Box>
      </Box>
    );
  };

  const getLedPatternConfig = () => {
    return (
      <Box width={1}>
        <Typography variant="caption">LED Pattern</Typography>
        <Select
          fullWidth
          size="small"
          value={props.config.ledPattern || ''}
          onChange={(e: SelectChangeEvent<any>) => {
            props.onConfigChange({
              ledPattern: e.target.value || '',
              bulbColor: '0',
              bulbColorTemp: 0,
            });
          }}
        >
          <MenuItem value="">&nbsp;</MenuItem>
          {ledPatternOptions.map((o) => (
            <MenuItem value={o.value} key={`led-pattern-${o.key}`}>
              {o.key}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const getMusicSyncConfig = () => {
    return (
      <Box width={1}>
        <Typography variant="caption">Music Sync Sensitivity</Typography>
        <Box px={2}>
          <Slider
            color="secondary"
            value={props.config.musicSyncSensitivity || 1}
            onChangeCommitted={(
              event: React.SyntheticEvent | Event,
              value: number | number[],
            ) => {
              props.onConfigChange({
                musicSyncSensitivity: value as number,
              });
            }}
            step={1}
            valueLabelDisplay="auto"
            marks={[
              { value: 1, label: '1' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
            ]}
          />
        </Box>
      </Box>
    );
  };

  const getSceneMode = () => {
    return (
      <Box width={1}>
        <Typography variant="caption">Scene Mode (SELECT LAST)</Typography>
        <Select
          fullWidth
          size="small"
          value={props.config.ledSceneMode || ''}
          onChange={(e: SelectChangeEvent<any>) => {
            props.onConfigChange({
              ledSceneMode: e.target.value || '',
              bulbColor: '0',
              bulbColorTemp: 0,
              ledPattern: 'custom',
            });
          }}
        >
          <MenuItem value="">&nbsp;</MenuItem>
          {sceneModeOptions.map((o) => (
            <MenuItem value={o.value} key={`scene-mode-${o.key}`}>
              {o.key}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const getColorPicker = () => {
    return (
      <IroColorPicker
        value={props.config.bulbColor}
        onChange={(nextColor: string) => {
          props.onConfigChange({
            bulbColor: nextColor.replaceAll('#', ''),
          });
        }}
      />
    );
  };

  return (
    <>
      <ExpandableSection title="RGB Strip Config" initiallyOpen>
        <Card sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader subheader="Color Selection" />
          <CardContent
            sx={{
              px: 4,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignContent: 'space-between',
              columnGap: 4,
            }}
          >
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
              sx={{ width: '100%' }}
            >
              {getColorPicker()}
            </Box>

            <Box display="flex" flexDirection="column" sx={{ width: '100%' }}>
              {getBrightnessConfig()}
              {getTemperatureConfig()}
              {getLedPatternConfig()}
              {getMusicSyncConfig()}
              {getSceneMode()}
            </Box>
          </CardContent>
        </Card>
      </ExpandableSection>
    </>
  );
};
